import { Box } from "@mui/material"
import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useState } from "react"

import type { InventoryLotEligibleQualityStatus } from "../../constants"
import { useProductInventoryContext } from "../../contexts"
import { useProductInventoryLotList } from "../../hooks"
import { useLotQualityStatusAutoRefresh } from "../../hooks/useLotQualityStatusAutoRefresh"
import { useLotQualityStatusRejectionNotice } from "../../hooks/useLotQualityStatusRejectionNotice"

import { ProductInventoryLotQualityStatusDialog } from "./ProductInventoryLotQualityStatusDialog"
import { ProductInventoryLotsTable } from "./ProductInventoryLotsTable"
import { ProductInventoryLotStatusErrorDialog } from "./ProductInventoryLotStatusErrorDialog"

import { PermissionActionType } from "@/constants/permissions"
import type { InventoryByLotItem } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export const ProductInventoryLotsTab: FC = () => {
  const { productId } = useParams({ from: "/app/inventory/products/$productId/_layout/lots" })
  const { product } = useProductInventoryContext()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.INVENTORY_MAINTENANCE])

  const [qualityStatusItem, setQualityStatusItem] = useState<InventoryByLotItem | undefined>()
  const [lotNotFoundMessage, setLotNotFoundMessage] = useState<string | undefined>()

  const {
    inventories,
    isFetching,
    error,
    sorting,
    tableContainerRef,
    refetch,
    refetchLoaded,
    handleSortingChange,
    handleScroll,
  } = useProductInventoryLotList({ productId: Number(productId) })

  useLotQualityStatusAutoRefresh(inventories, refetchLoaded)
  const { trackQualityStatusChange } = useLotQualityStatusRejectionNotice(inventories)

  const handleQualityStatusClose = () => setQualityStatusItem(undefined)

  const handleQualityStatusSuccess = async (status: InventoryLotEligibleQualityStatus) => {
    if (qualityStatusItem) trackQualityStatusChange(qualityStatusItem, status)
    setQualityStatusItem(undefined)

    // The in-place refetch is skipped while another fetch is running; reload so the lot never shows a stale status.
    const isRefetched = await refetchLoaded()
    if (!isRefetched) refetch()
  }

  const handleLotNotFound = (message: string) => {
    setQualityStatusItem(undefined)
    setLotNotFoundMessage(message)
  }

  const handleTransfer = (_inventory: InventoryByLotItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleCoaView = (_inventory: InventoryByLotItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  return (
    <Box className='flex min-h-0 flex-1 overflow-y-auto'>
      <ProductInventoryLotsTable
        product={product}
        data={inventories}
        loading={isFetching}
        error={error}
        sorting={sorting}
        containerRef={tableContainerRef}
        canMaintain={canMaintain}
        onSortingChange={handleSortingChange}
        onScroll={handleScroll}
        onQualityStatusChange={setQualityStatusItem}
        onTransfer={handleTransfer}
        onCoaView={handleCoaView}
      />
      {qualityStatusItem && (
        <ProductInventoryLotQualityStatusDialog
          item={qualityStatusItem}
          productId={Number(productId)}
          onClose={handleQualityStatusClose}
          onSuccess={handleQualityStatusSuccess}
          onLotNotFound={handleLotNotFound}
        />
      )}
      {lotNotFoundMessage && (
        <ProductInventoryLotStatusErrorDialog
          message={lotNotFoundMessage}
          onClose={() => setLotNotFoundMessage(undefined)}
        />
      )}
    </Box>
  )
}
